import * as basketDto from '@epicuro-next/data/baskets-dto';
import {
  BasketAccountReference,
  BasketPatchItem,
  UpdateBasketItemSource,
} from '@epicuro-next/data/baskets-dto';
import { getSelectedShippingOptionId } from '@epicuro-next/data/baskets-grouping';
import { generateId, getMergedBasketItemSource } from '@epicuro-next/utilities';
import {
  BasketAccountAddress,
  BasketAccountDelivery,
  BasketHeader,
  BasketUpdateRequest,
  BasketUpdateResult,
  OrderType,
} from 'graphql-api/baskets-api-interfaces';

import { mapValidationResult } from './validation.mapping';

/**
 * Maps hierarchical basket update request to model accepted by Baskets API.
 */
export function mapBasketUpdateRequest(
  basketUpdateRequest: BasketUpdateRequest,
  basket: basketDto.ExtendedDtoBasket,
): basketDto.BasketPatchModel {
  const updatedBasketAccounts = mapBasketAccounts(basketUpdateRequest, basket);
  const basketAccountsMap = getBasketAccountsMap(updatedBasketAccounts);

  const itemsToInsertOrUpdate = getBasketItemsToAddOrUpdate(
    basketUpdateRequest.deliveryItems,
    basketAccountsMap,
  );

  const basketAccounts = clearBasketAccountsDropshipForMdo(
    itemsToInsertOrUpdate,
    updatedBasketAccounts,
  );

  return {
    basketHeader: mapBasketHeader(
      basketUpdateRequest.basketHeader,
      basketAccounts,
    ),
    itemsToInsertOrUpdate,
    itemIdsToRemove: getBasketItemsToRemove(basketUpdateRequest.deliveryItems),
    etag: basket?.etag,
  };
}

export function mapBasketUpdateAccountsRequest(
  basket: basketDto.DtoBasket,
  accountsToAdd: BasketAccountAddress[],
  accountsToRemove: BasketAccountAddress[],
): basketDto.BasketPatchModel {
  const basketAccountsToAdd = getBasketAccountsToAdd(accountsToAdd);
  const referenceBasketAccount = getReferencedBasketAccount(basket);
  const itemsToAdd = basketAccountsToAdd.flatMap((e) =>
    getBasketItemsToAdd(referenceBasketAccount, e.basketAccountId, basket),
  );

  const basketAccountsToRemove = getBasketAccountsToRemove(
    basket,
    accountsToRemove,
  );
  const itemIdsToRemove = basket.items
    .filter((e) => basketAccountsToRemove.indexOf(e.basketAccountId) >= 0)
    .map((e) => e.itemId);

  const selectedShippingOptionId = getSelectedShippingOptionId(
    basket.basketAccounts,
  );

  const updatedBasketAccounts = [
    ...basketAccountsToAdd,
    ...basket.basketAccounts
      .filter((e) => basketAccountsToRemove.indexOf(e.basketAccountId) === -1)
      .map((e) => ({
        basketAccountId: e.basketAccountId,
        accountId: e.account.accountId,
        addressId: e.address.addressId,
        clientReferenceNumber: e.clientReferenceNumber,
        alternativeAddress: e.alternativeAddress,
      })),
  ].map((account) => ({
    ...account,
    selectedShippingOptionId,
  }));

  const basketItems = itemsToAdd.concat(
    basket.items.filter(
      (e) => basketAccountsToRemove.indexOf(e.basketAccountId) === -1,
    ) as unknown as BasketPatchItem[],
  );

  const basketAccounts = clearBasketAccountsDropshipForMdo(
    basketItems,
    updatedBasketAccounts,
  );

  return {
    basketHeader: {
      basketId: basket.basketId,
      orderReferenceNumber: basket.orderReferenceNumber,
      clientReferenceNumber: basket.clientReferenceNumber,
      propertyBag: basket.propertyBag,
      basketAccounts,
    },
    itemsToInsertOrUpdate: itemsToAdd,
    itemIdsToRemove,
  };
}

function getBasketAccountsToAdd(accountsToAdd: BasketAccountAddress[]) {
  return accountsToAdd.map((e) => ({
    basketAccountId: generateId(),
    accountId: e.accountId,
    addressId: e.addressId,
    alternativeAddress: e.alternativeAddress,
  }));
}

function getBasketItemsToAdd(
  referenceBasketAccountId: string,
  basketAccountId: string,
  basket: basketDto.DtoBasket,
): BasketPatchItem[] {
  return basket.items
    .filter(
      (item) =>
        item.requestedQuantity > 0 &&
        item.basketAccountId === referenceBasketAccountId,
    )
    .map((item) => {
      const delivery = basket.basketAccounts
        .find((ba) => ba.basketAccountId === referenceBasketAccountId)
        ?.deliveries.find((d) => d.deliveryId === item.deliveryId);
      return {
        brandId: item.brandId,
        productId: item.productId,
        basketAccountId,
        requestedQuantity: item.requestedQuantity,
        requestedDeliveryDate: item.requestedDeliveryDate,
        displayDateFrom: item.displayDateFrom,
        displayDateTo: item.displayDateTo,
        orderType: item.orderType,
        consignmentStartDate: delivery?.consignmentStartDate,
        consignmentEndDate: delivery?.consignmentEndDate,
        ...getMergedBasketItemSource(
          {
            type: UpdateBasketItemSource.AddRemoveShops,
          },
          {
            basketId: [basket.basketId],
          },
          item.basketItemOrigin,
        ),
      };
    });
}

function getBasketAccountsToRemove(
  basket: basketDto.DtoBasket,
  accountsToRemove: BasketAccountAddress[],
) {
  const basketAccounts = basket.basketAccounts.map((e) => ({
    basketAccountId: e.basketAccountId,
    accountId: e.account.accountId,
    addressId: e.address.addressId,
  }));

  return (
    basketAccounts
      .filter(
        (e) =>
          accountsToRemove.findIndex(
            (c) => c.accountId === e.accountId && c.addressId === e.addressId,
          ) >= 0,
      )
      .map((e) => e.basketAccountId) ?? []
  );
}

function getReferencedBasketAccount(basket: basketDto.DtoBasket) {
  const ids = basket.items.map((e) => e.basketAccountId);
  return (
    ids.pop() ||
    (basket.basketAccounts.length > 0
      ? basket.basketAccounts[0].basketAccountId
      : '')
  );
}

function mapBasketHeader(
  basketHeader: BasketHeader,
  basketAccounts: basketDto.BasketAccountReference[],
): basketDto.BasketHeader {
  return {
    ...basketHeader,
    basketAccounts,
  };
}

function mapBasketAccounts(
  basketUpdateRequest: BasketUpdateRequest,
  basket: basketDto.DtoBasket,
): basketDto.BasketAccountReference[] {
  const selectedShippingOptionId =
    basketUpdateRequest.basketHeader.selectedShippingOptionId;
  const existingBasketAccounts = basket.basketAccounts.map((basketAccount) => ({
    basketAccountId: basketAccount.basketAccountId,
    accountId: basketAccount.account?.accountId,
    addressId: basketAccount.address?.addressId ?? basketAccount.addressId,
    alternativeAddress: basketAccount.alternativeAddress,
    clientReferenceNumber: getBasketAccountClientReferenceNumber(
      basketAccount,
      basketUpdateRequest,
    ),
    deliveries: mapDeliveries(basketAccount, basketUpdateRequest),
    ...(selectedShippingOptionId ? { selectedShippingOptionId } : {}),
  }));

  if (!basketUpdateRequest.deliveryItems.length) {
    return existingBasketAccounts;
  }

  const newBasketAccounts = basketUpdateRequest.deliveryItems
    .filter(
      (deliveryItem) =>
        existingBasketAccounts.findIndex(
          (basketAccount) =>
            basketAccount.accountId === deliveryItem.accountId &&
            basketAccount.addressId === deliveryItem.addressId,
        ) === -1,
    )
    .map((deliveryItem) => ({
      basketAccountId: generateId(),
      accountId: deliveryItem.accountId,
      addressId: deliveryItem.addressId,
      alternativeAddress: deliveryItem.alternativeAddress,
      clientReferenceNumber: deliveryItem.clientReferenceNumber,
      selectedShippingOptionId: getSelectedShippingOptionId(
        basket.basketAccounts,
      ),
    }));

  return [...existingBasketAccounts, ...newBasketAccounts].filter(
    (e) => e.accountId && e.addressId,
  );
}

function mapDeliveries(
  basketAccount: basketDto.BasketAccount,
  basketUpdateRequest: BasketUpdateRequest,
): basketDto.Delivery[] {
  return basketAccount.deliveries.map((delivery) => ({
    ...delivery,
    clientReferenceNumber:
      basketUpdateRequest.deliveries?.find(
        (updateDelivery) => updateDelivery.deliveryId === delivery.deliveryId,
      )?.clientReferenceNumber ?? delivery.clientReferenceNumber,
  }));
}

function getBasketAccountClientReferenceNumber(
  basketAccount: basketDto.BasketAccount,
  basketUpdateRequest: BasketUpdateRequest,
) {
  const addressId = basketAccount.address?.addressId ?? basketAccount.addressId;
  return (
    basketUpdateRequest.deliveryItems?.find(
      (e) =>
        e.accountId === basketAccount.account?.accountId &&
        e.addressId === addressId,
    )?.clientReferenceNumber ?? basketAccount.clientReferenceNumber
  );
}

function getBasketAccountsMap(
  basketAccounts: basketDto.BasketAccountReference[],
) {
  const basketAccountsMap = new Map<string, basketDto.BasketAccountReference>();
  basketAccounts
    .filter((e) => e.accountId && e.addressId)
    .forEach((e) => {
      basketAccountsMap.set(e.addressId, e);
    });

  return basketAccountsMap;
}

function getBasketItemsToAddOrUpdate(
  deliveryItems: BasketAccountDelivery[],
  basketAccountsMap: Map<string, basketDto.BasketAccountReference>,
): basketDto.BasketPatchItem[] {
  return deliveryItems.flatMap((basketAccount) => {
    const basketAccountReference = basketAccountsMap.get(
      basketAccount.addressId,
    );
    if (!basketAccountReference) {
      throw Error('Bakset account has not been generated.');
    }

    return basketAccount.items
      .filter((e) => e.requestedQuantity > 0)
      .map((e) => ({
        ...e,
        itemId: e.basketItemId,
        orderType: mapOrderTypeToDto(e.orderType),
        basketAccountId: basketAccountReference.basketAccountId,
        inventoryId: e.inventoryId,
      }));
  });
}

function getBasketItemsToRemove(
  deliveryItems: BasketAccountDelivery[],
): string[] {
  const items = deliveryItems.flatMap((e) => e.items);
  const withoutQuantity = items
    .filter((e) => !!e.basketItemId && e.requestedQuantity === 0)
    .filter(
      (e) =>
        !items.some(
          (i) => i.requestedQuantity && i.basketItemId === e.basketItemId,
        ),
    );

  return withoutQuantity.map((e) => e.basketItemId || '');
}

export function mapOrderTypeToDto(
  orderType?: OrderType | null,
): basketDto.OrderType | undefined {
  if (orderType) {
    return orderType === OrderType.Future
      ? basketDto.OrderType.Future
      : basketDto.OrderType.Now;
  }
  return undefined;
}

export function mapOrderTypeToModel(
  orderType?: basketDto.OrderType | null,
): OrderType | undefined {
  if (orderType) {
    return orderType === basketDto.OrderType.Future
      ? OrderType.Future
      : OrderType.Now;
  }
  return undefined;
}

/**
 * Creates update basket result from response.
 */
export function createBasketUpdateResult(
  basket: basketDto.DtoBasket,
): BasketUpdateResult | null {
  const validationResults =
    basket.validationResults?.map(mapValidationResult) ?? [];

  const items = basket.items?.map((item) => ({
    basketItemId: item.itemId,
    brandId: item.brandId,
    productId: item.productId,
    requestedQuantity: item.requestedQuantity,
    requestedDeliveryDate: item.requestedDeliveryDate,
    displayDateFrom: item.displayDateFrom,
    displayDateTo: item.displayDateTo,
    orderType: mapOrderTypeToModel(item.orderType),
    validationResults: item.validationResults?.map(mapValidationResult) ?? [],
    consignmentStartDate: item.consignmentStartDate,
    consignmentEndDate: item.consignmentEndDate,
    inventoryId: item.inventoryId,
  }));

  return {
    userSpecifiedGroupedDelivery: basket.userSpecifiedGroupedDelivery,
    validationResults,
    items,
  };
}

function clearBasketAccountsDropshipForMdo(
  basketItems: BasketPatchItem[],
  basketAccounts: BasketAccountReference[],
): BasketAccountReference[] {
  const basketAccountsWithItems = basketItems
    .filter((item) => item.requestedQuantity > 0)
    .map((item) => item.basketAccountId);
  const isMdo =
    basketAccounts.filter((basketAccount) =>
      basketAccountsWithItems.includes(basketAccount.basketAccountId),
    ).length > 1;
  return basketAccounts.map((basketAccount) => ({
    ...basketAccount,
    alternativeAddress: isMdo ? null : basketAccount.alternativeAddress,
  }));
}
